import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  Eye,
  MapPin,
  Sparkles,
  Stethoscope,
  TrendingUp,
  Users,
} from "lucide-react";
import type { InsightBucket, InsightsReport } from "@clinic/types";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

function BigStat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card className="flex items-center gap-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-subtle/40 text-accent-light">
        <Icon size={20} />
      </div>
      <div>
        <div className="text-xl font-semibold">{value}</div>
        <div className="text-xs text-text-secondary">{label}</div>
        {hint && <div className="text-xs text-text-tertiary">{hint}</div>}
      </div>
    </Card>
  );
}

function BarList({ data, empty }: { data: InsightBucket[]; empty: string }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  if (data.length === 0) {
    return <p className="py-6 text-center text-sm text-text-tertiary">{empty}</p>;
  }
  return (
    <div className="space-y-2.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="w-28 shrink-0 truncate text-sm text-text-secondary" title={d.label}>
            {d.label}
          </span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-pill bg-bg-tertiary">
            <div
              className="h-full rounded-pill bg-accent"
              style={{ width: `${(d.count / max) * 100}%` }}
            />
          </div>
          <span className="w-8 shrink-0 text-right text-sm font-medium tabular-nums">{d.count}</span>
        </div>
      ))}
    </div>
  );
}

function BotCard({ report }: { report: InsightsReport }) {
  return (
    <Card
      className="relative overflow-hidden border-accent-subtle"
      style={{ background: "linear-gradient(135deg, var(--accent-subtle) 0%, var(--bg-secondary) 60%)" }}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-accent text-on-accent">
          <Sparkles size={20} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-md font-semibold">Clinic Insights Bot</h2>
            <Badge tone="active">scanned {report.totalPatients} patients</Badge>
          </div>
          <p className="mt-0.5 text-sm text-text-secondary">{report.headline}</p>
          <ul className="mt-3 space-y-1.5">
            {report.summary.map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-pill bg-accent" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-text-tertiary">
            Generated {format(new Date(report.generatedAt), "d MMM yyyy, HH:mm")} · rule-based scan of
            patients, visits & appointments
          </p>
        </div>
      </div>
    </Card>
  );
}

export function InsightsPage() {
  const insights = useQuery({
    queryKey: ["insights"],
    queryFn: () => api.insights.report(),
    refetchInterval: 60_000,
  });

  if (insights.isLoading) {
    return (
      <div>
        <PageHeader title="Insights" subtitle="Doctor analytics" />
        <Skeleton className="mb-6 h-40" />
        <div className="grid gap-6 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-56" />
          ))}
        </div>
      </div>
    );
  }
  const r = insights.data;
  if (!r) return null;

  return (
    <div>
      <PageHeader title="Insights" subtitle="A scan of your practice — patients, follow-ups & trends" />

      <div className="mb-6">
        <BotCard report={r} />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <BigStat icon={CalendarClock} label="Coming this week" value={r.thisWeekCount} />
        <BigStat icon={CalendarDays} label="Coming next week" value={r.nextWeekCount} />
        <BigStat icon={Stethoscope} label="Follow-ups due" value={r.followUpsDue.length} />
        <BigStat
          icon={Users}
          label="Active patients"
          value={r.totalPatients}
          hint={`+${r.newPatientsThisMonth} this month`}
        />
        <BigStat icon={TrendingUp} label="Average age" value={r.averageAge} hint="years" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Follow-ups due */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope size={16} className="text-accent-light" /> Follow-ups to action
          </CardTitle>
          {r.followUpsDue.length === 0 ? (
            <p className="py-6 text-center text-sm text-text-tertiary">Nothing outstanding 🎉</p>
          ) : (
            <div className="space-y-2">
              {r.followUpsDue.map((f) => (
                <Link
                  key={f.patientId}
                  to="/patients/$patientId"
                  params={{ patientId: f.patientId }}
                  className="flex items-center justify-between gap-3 rounded-lg border bg-bg px-3 py-2.5 transition hover:bg-bg-tertiary"
                  style={{ borderColor: "var(--accent-subtle)" }}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{f.fullName}</div>
                    <div className="truncate text-xs text-text-tertiary">{f.reason}</div>
                  </div>
                  <span className="shrink-0 text-xs text-text-secondary">
                    {format(new Date(f.date), "d MMM")}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* IOP watch */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-warning" /> Elevated IOP — glaucoma watch
          </CardTitle>
          {r.iopWatch.length === 0 ? (
            <p className="py-6 text-center text-sm text-text-tertiary">
              No patients above 21 mmHg.
            </p>
          ) : (
            <div className="space-y-2">
              {r.iopWatch.map((p) => (
                <Link
                  key={p.patientId}
                  to="/patients/$patientId"
                  params={{ patientId: p.patientId }}
                  className="flex items-center justify-between gap-3 rounded-lg border bg-bg px-3 py-2.5 transition hover:bg-bg-tertiary"
                  style={{ borderColor: "var(--accent-subtle)" }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={15} className="text-text-tertiary" />
                    <span className="text-sm font-medium">{p.fullName}</span>
                  </div>
                  <Badge tone={p.iop >= 25 ? "cancelled" : "pending"}>
                    {p.iop} mmHg · {p.eye}
                  </Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Age distribution */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <Users size={16} className="text-accent-light" /> Age distribution
          </CardTitle>
          <BarList data={r.ageDistribution} empty="No patient data." />
        </Card>

        {/* Geography */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <MapPin size={16} className="text-accent-light" /> Where patients come from
          </CardTitle>
          <BarList data={r.geographic} empty="No address data." />
        </Card>

        {/* Diagnoses */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <Eye size={16} className="text-accent-light" /> Top diagnoses
          </CardTitle>
          <BarList data={r.diagnoses} empty="No exam records yet." />
        </Card>

        {/* Operations */}
        <Card>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp size={16} className="text-accent-light" /> Operations (30 days)
          </CardTitle>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Cancellation rate</span>
              <span className="font-medium">{Math.round(r.cancellationRate * 100)}%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">No-show rate</span>
              <span className="font-medium">{Math.round(r.noShowRate * 100)}%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Busiest upcoming day</span>
              <span className="font-medium">
                {r.busiestDay ? `${r.busiestDay.label} (${r.busiestDay.count})` : "—"}
              </span>
            </div>
            <div className="border-t pt-3" style={{ borderColor: "var(--accent-subtle)" }}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                Appointment mix
              </div>
              <BarList data={r.appointmentTypes} empty="No appointments." />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
